import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { rhythmApi, centralApi } from "@/lib/api";
import { useCentralState } from "@/context/CentralStateContext";
import { weekKey, isoLocal } from "@/lib/rhythm";
import { toast } from "sonner";

const RhythmStateContext = createContext(null);

export const RhythmStateProvider = ({ children }) => {
  const { fin, life, wellness, loading: centersLoading } = useCentralState();
  const [store, setStore] = useState(null); // { reviews:{}, plans:{} }
  const [pillarStore, setPillarStore] = useState({ measures: {} });
  const [week, setWeek] = useState(weekKey(isoLocal(new Date())));
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      rhythmApi.get().catch(() => ({ state: { reviews: {}, plans: {} } })),
      centralApi.getPillars().catch(() => ({ state: { measures: {} } })),
    ]).then(([r, p]) => {
      if (!mounted) return;
      const s = r.state || {};
      setStore({ reviews: s.reviews || {}, plans: s.plans || {} });
      setPillarStore(p.state && p.state.measures ? p.state : { measures: {} });
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const persist = useCallback((next) => {
    setStore(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { rhythmApi.save(next).catch(() => toast.error("Couldn't save")); }, 600);
  }, []);

  const updateReview = useCallback((mutator) => {
    setStore((prev) => {
      const cur = prev || { reviews: {}, plans: {} };
      const rev = mutator({ ...(cur.reviews[week] || {}) });
      const next = { ...cur, reviews: { ...cur.reviews, [week]: { ...rev, updatedAt: new Date().toISOString() } } };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => rhythmApi.save(next).catch(() => toast.error("Couldn't save")), 600);
      return next;
    });
  }, [week]);

  const updatePlan = useCallback((mutator) => {
    setStore((prev) => {
      const cur = prev || { reviews: {}, plans: {} };
      const pl = mutator({ ...(cur.plans[week] || {}) });
      const next = { ...cur, plans: { ...cur.plans, [week]: { ...pl, updatedAt: new Date().toISOString() } } };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => rhythmApi.save(next).catch(() => toast.error("Couldn't save")), 600);
      return next;
    });
  }, [week]);

  return (
    <RhythmStateContext.Provider value={{
      store, pillarStore, week, setWeek, loading: loading || centersLoading,
      fin, life, wellness, persist, updateReview, updatePlan,
      review: store?.reviews?.[week], plan: store?.plans?.[week],
    }}>
      {children}
    </RhythmStateContext.Provider>
  );
};

export const useRhythm = () => useContext(RhythmStateContext);
